/**
 * Home Assistant colour values → openHASP/LVGL hex colours.
 *
 * HA exposes several colour spaces on light entities. Keeping the conversion
 * rules here gives the editor/preview and the HA template generator one place
 * to agree on the supported input shapes.
 */

export const COLOR_ATTRIBUTES = ['rgb_color', 'rgbw_color', 'rgbww_color', 'hs_color', 'xy_color'];

function clamp(value, min = 0, max = 255) {
  return Math.max(min, Math.min(max, Number(value) || 0));
}

function hex({ r, g, b }) {
  const channel = value => Math.round(clamp(value)).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function rgbwToRgb(value) {
  const white = Number(value[3]) || 0;
  return { r: clamp(Number(value[0]) + white), g: clamp(Number(value[1]) + white), b: clamp(Number(value[2]) + white) };
}

/**
 * Normalize an HA colour value to a six-digit hex string.
 * Returns null for missing or malformed values.
 */
export function normalizeColor(value, attribute = 'rgb_color') {
  if (!Array.isArray(value) || value.length < 2) return null;

  switch (attribute) {
    case 'rgb_color':
      if (value.length < 3) return null;
      return hex({ r: value[0], g: value[1], b: value[2] });

    case 'rgbw_color':
      if (value.length < 4) return null;
      return hex(rgbwToRgb(value));

    case 'rgbww_color': {
      if (value.length < 5) return null;
      const white = ((Number(value[3]) || 0) + (Number(value[4]) || 0)) / 2;
      return hex({ r: Number(value[0]) + white, g: Number(value[1]) + white, b: Number(value[2]) + white });
    }

    case 'hs_color': {
      if (value.length < 2) return null;
      const h = ((Number(value[0]) || 0) % 360 + 360) % 360;
      const s = clamp(value[1], 0, 100) / 100;
      const sector = h / 60;
      const i = Math.floor(sector);
      const f = sector - i;
      const p = 1 - s;
      const q = 1 - s * f;
      const t = 1 - s * (1 - f);
      const channels = [[1, t, p], [q, 1, p], [p, 1, t], [p, q, 1], [t, p, 1], [1, p, q]][i % 6];
      return hex({ r: channels[0] * 255, g: channels[1] * 255, b: channels[2] * 255 });
    }

    case 'xy_color': {
      if (value.length < 2) return null;
      const x = Number(value[0]) || 0;
      const y = Number(value[1]) || 0;
      if (y <= 0) return '#000000';
      const Y = 1;
      const X = (Y * x) / y;
      const Z = (Y * (1 - x - y)) / y;
      // sRGB D65 conversion. Negative channels are clipped before gamma.
      const linear = [
        3.2406 * X - 1.5372 * Y - 0.4986 * Z,
        -0.9689 * X + 1.8758 * Y + 0.0415 * Z,
        0.0557 * X - 0.2040 * Y + 1.0570 * Z,
      ].map(channel => clamp(channel, 0, 1));
      const gamma = channel => (channel <= 0.0031308 ? 12.92 * channel : 1.055 * (channel ** (1 / 2.4)) - 0.055) * 255;
      return hex({ r: gamma(linear[0]), g: gamma(linear[1]), b: gamma(linear[2]) });
    }

    default:
      return null;
  }
}

/**
 * Render the supported HA colour shapes as a Jinja expression. The rgb path
 * intentionally keeps the historic byte-for-byte output for compatibility.
 */
export function colorAttributeToJinja(valueExpression, attribute = 'rgb_color') {
  if (attribute === 'rgb_color' || !COLOR_ATTRIBUTES.includes(attribute)) {
    return (
      `{% set rgb = ${valueExpression} %}` +
      `{{ "#%02x%02x%02x" | format(rgb[0], rgb[1], rgb[2]) if rgb else "#ffffff" }}`
    );
  }

  if (attribute === 'rgbw_color') {
    return (
      `{% set rgbw = ${valueExpression} %}` +
      `{% set w = rgbw[3] if rgbw else 0 %}` +
      `{{ "#%02x%02x%02x" | format([[rgbw[0] + w, 255] | min, 0] | max, [[rgbw[1] + w, 255] | min, 0] | max, [[rgbw[2] + w, 255] | min, 0] | max) if rgbw else "#ffffff" }}`
    );
  }

  if (attribute === 'rgbww_color') {
    return (
      `{% set rgbww = ${valueExpression} %}` +
      `{% set w = ((rgbww[3] + rgbww[4]) / 2) if rgbww else 0 %}` +
      `{{ "#%02x%02x%02x" | format([[rgbww[0] + w, 255] | min, 0] | max, [[rgbww[1] + w, 255] | min, 0] | max, [[rgbww[2] + w, 255] | min, 0] | max) if rgbww else "#ffffff" }}`
    );
  }

  if (attribute === 'hs_color') {
    return (
      `{% set hs = ${valueExpression} %}` +
      `{% set h = ((hs[0] % 360) / 60) if hs else 0 %}` +
      `{% set s = ((hs[1] / 100) if hs else 0) %}` +
      `{% set i = h | int %}{% set f = h - i %}` +
      `{% set p = 1 - s %}{% set q = 1 - s * f %}{% set t = 1 - s * (1 - f) %}` +
      `{% set c = [[1,t,p],[q,1,p],[p,1,t],[p,q,1],[t,p,1],[1,p,q]][i % 6] %}` +
      `{{ "#%02x%02x%02x" | format((c[0] * 255) | int, (c[1] * 255) | int, (c[2] * 255) | int) if hs else "#ffffff" }}`
    );
  }

  // xy_color: convert CIE xyY (with Y=1) to linear sRGB. This is deliberately
  // kept in the same normalizer as the other HA colour formats.
  return (
    `{% set xy = ${valueExpression} %}` +
    `{% set x = xy[0] if xy else 0 %}{% set y = xy[1] if xy else 0 %}` +
    `{% set X = (x / y) if y else 0 %}{% set Y = 1 %}{% set Z = ((1 - x - y) / y) if y else 0 %}` +
    `{% set r = [3.2406 * X - 1.5372 * Y - 0.4986 * Z, 1] | min %}` +
    `{% set g = [-0.9689 * X + 1.8758 * Y + 0.0415 * Z, 1] | min %}` +
    `{% set b = [0.0557 * X - 0.2040 * Y + 1.0570 * Z, 1] | min %}` +
    `{{ "#%02x%02x%02x" | format(([r, 0] | max * 255) | int, ([g, 0] | max * 255) | int, ([b, 0] | max * 255) | int) if xy else "#ffffff" }}`
  );
}
